// services/chatStream/seqTracker.ts

/**
 * What to do with an event given the highest `seq` already applied.
 *
 * - `apply` — the next event in order, or the first event of a turn.
 * - `gap` — seq jumped ahead. The event is still applied (dropping it would
 *   lose real content), but the turn is marked degraded so the UI can offer a
 *   retry instead of silently rendering a stream with a hole in it.
 * - `duplicate` — seq is at or behind what we already applied, i.e. a repeat or
 *   an out-of-order delivery. Ignored, because re-applying it would double a
 *   text delta or a block.
 *
 * `lastSeq` is `null` until the first event of a turn arrives. The base is
 * seeded from that event rather than assumed: ora-be ADR-0006 pins `turn.start` to
 * seq 1, but a client that assumed a fixed base would silently discard the
 * whole turn if a server ever numbered from 0.
 */
export type SeqDecision = 'apply' | 'gap' | 'duplicate';

export const decideSeq = (lastSeq: number | null, seq: number): SeqDecision => {
  if (lastSeq === null) return 'apply';
  if (seq <= lastSeq) return 'duplicate';
  if (seq > lastSeq + 1) return 'gap';
  return 'apply';
};
